import React, { useState, useEffect } from 'react';
import {
  Shield,
  Mail,
  Loader2,
  AlertCircle,
  CheckCircle,
  Building2,
  ArrowLeft,
  KeyRound
} from 'lucide-react';
import {
  signInWithMagicLink,
  signInWithGoogle,
  requestLoginCode,
  verifyLoginCode,
  isSupabaseConfigured,
  getCurrentSession
} from '../services/supabase';
import { useAppStore } from '../store/useAppStore';

interface LoginViewProps {
  onSuccess: () => void;
  onBack: () => void;
}

export function LoginView({ onSuccess, onBack }: LoginViewProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* 画面の段。'codeSent' は6桁コード、'linkSent' は従来のマジックリンク。
     リンク方式は廃止せず選択肢として残す（法人環境ではコードの方が通る）。 */
  const [stage, setStage] = useState<'form' | 'codeSent' | 'linkSent'>('form');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isLinkLoading, setIsLinkLoading] = useState(false);
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  /* 検証成功後は onAuthStateChange('SIGNED_IN') が App 側で画面を差し替える。
     万一それが来ない場合に画面が固まったままになるのを防ぐ。 */
  const [waitingSignIn, setWaitingSignIn] = useState(false);
  /* サーバ側の再送間隔(60秒)と一致させ、429 を日常的に見せない。 */
  const [resendCooldown, setResendCooldown] = useState(0);

  const { setUser, setOrganization, addToast } = useAppStore();
  const isConfigured = isSupabaseConfigured();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (!isConfigured) {
        // Demo mode: simulate login
        await new Promise(resolve => setTimeout(resolve, 1000));

        setUser({
          id: 'demo-user',
          email: email || 'demo@example.com',
          name: 'Demo User',
          organizationId: 'demo-org',
          role: 'admin',
          createdAt: new Date().toISOString()
        });

        setOrganization({
          id: 'demo-org',
          name: 'Demo Organization',
          plan: 'standard',
          qrLimit: 10,
          qrUsed: 3,
          seats: 1,
          createdAt: new Date().toISOString()
        });

        addToast({
          type: 'info',
          message: 'デモモード: デモアカウントでログインしました'
        });

        onSuccess();
        return;
      }

      /* 既定は6桁コード。マジックリンクは法人のメール環境で
         本人より先に開かれて使い切られることがあるため。 */
      const res = await requestLoginCode(email);
      if (!res.success) {
        setError(res.message ?? 'メールを送信できませんでした。');
        return;
      }

      setStage('codeSent');
      setCode('');
      setResendCooldown(60);
      addToast({ type: 'success', message: '認証コードをメールで送信しました' });
    } catch {
      setError('メールを送信できませんでした。時間をおいて再度お試しください。');
    } finally {
      setIsLoading(false);
    }
  };

  /** 従来のマジックリンク。選択肢として残す。 */
  const handleSendLink = async () => {
    setError(null);
    if (!email) {
      setError('メールアドレスを入力してください。');
      return;
    }
    setIsLinkLoading(true);
    try {
      const { success, error: authError } = await signInWithMagicLink(email);
      if (authError) throw authError;
      if (!success) throw new Error('メールの送信に失敗しました');
      setStage('linkSent');
      addToast({ type: 'success', message: 'ログインリンクをメールで送信しました' });
    } catch {
      setError('メールを送信できませんでした。時間をおいて再度お試しください。');
    } finally {
      setIsLinkLoading(false);
    }
  };

  /** 6桁コードを検証する。 */
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsVerifying(true);
    try {
      const res = await verifyLoginCode(email, code);
      if (!res.success) {
        setError(res.message ?? 'ログインできませんでした。');
        return;
      }
      /* 成功。画面遷移は App 側の onAuthStateChange('SIGNED_IN') が行う。
         ここで onSuccess() を呼ぶと二重遷移になるため呼ばない。 */
      setWaitingSignIn(true);
    } finally {
      setIsVerifying(false);
    }
  };

  /** コードを再送する。サーバ側も60秒間隔なので画面側もそろえる。 */
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setError(null);
    setIsLoading(true);
    try {
      const res = await requestLoginCode(email);
      if (!res.success) {
        setError(res.message ?? 'メールを送信できませんでした。');
        return;
      }
      setCode('');
      setResendCooldown(60);
      addToast({ type: 'success', message: '認証コードを再送しました' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = () => {
    setStage('form');
    setError(null);
  };

  /* 再送クールダウンの秒読み。 */
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  /* SIGNED_IN が来なければ 10 秒で案内を出す。App.tsx の authTimeout は
     URL にトークンがある経路専用で、ここには効かないため自前で持つ。 */
  useEffect(() => {
    if (!waitingSignIn) return;
    const t = setTimeout(() => {
      setWaitingSignIn(false);
      setError('ログイン処理が完了しませんでした。ページを再読み込みしてお試しください。');
    }, 10000);
    return () => clearTimeout(t);
  }, [waitingSignIn]);

  const handleGoogle = async () => {
    setError(null);
    setIsGoogleLoading(true);
    try {
      // signInWithGoogle triggers a full-page redirect to Google on success,
      // so we only clear the loading state when it fails.
      const { success, error: authError } = await signInWithGoogle();
      if (authError) throw authError;
      if (!success) throw new Error('Googleログインを開始できませんでした');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
      setIsGoogleLoading(false);
    }
  };

  // 認証コード入力画面
  if (stage === 'codeSent') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-industrial-50 to-safety-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <button onClick={onBack} className="flex items-center gap-2 hover:opacity-80 transition">
              <img src="/icon-512x512.png" alt="" className="w-10 h-10 rounded-xl" />
              <span className="text-xl font-bold text-industrial-900">安全ポスト＋</span>
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-safety-50 flex items-center justify-center">
                <KeyRound className="w-7 h-7 text-safety-600" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-industrial-900 text-center mb-2">
              認証コードを入力
            </h2>
            <p className="text-sm text-slate-600 text-center mb-1">
              <span className="font-semibold text-industrial-900">{email}</span> に
            </p>
            <p className="text-sm text-slate-600 text-center mb-6">
              6桁の認証コードをお送りしました。
            </p>

            <form onSubmit={handleVerifyCode} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                /* 全角数字や空白混じりの貼り付けを受け付ける。
                   「１２３ ４５６」のような形でも通るようにする。 */
                onChange={(e) => setCode(e.target.value.normalize('NFKC').replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                aria-label="認証コード（6桁）"
                autoFocus
                className="w-full text-center tracking-[0.4em] text-2xl font-bold font-mono px-4 py-4 border-2 border-slate-200 rounded-xl focus:border-safety-500 focus:outline-none"
              />

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={code.length !== 6 || isVerifying || waitingSignIn}
                className="w-full bg-safety-500 hover:bg-safety-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-xl transition flex items-center justify-center gap-2"
              >
                {isVerifying || waitingSignIn ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>ログイン中...</span>
                  </>
                ) : (
                  <span>コードでログイン</span>
                )}
              </button>
            </form>

            <div className="mt-6 p-4 bg-slate-50 rounded-lg text-xs text-slate-600 leading-relaxed">
              <p className="font-bold text-industrial-900 mb-1">うまくいかない場合</p>
              ・メールの到着まで<strong className="text-industrial-900">数分かかることがあります</strong>。少しお待ちください。<br />
              ・迷惑メールフォルダもご確認ください。<br />
              ・コードは<strong className="text-industrial-900">約60分間</strong>有効です。<br />
              ・コードは誰にも教えないでください。当社からお尋ねすることはありません。
            </div>

            <div className="mt-4 flex flex-col gap-2">
              {/* 再送は「前のコードを無効にする」副作用がある。メールが遅延する環境では
                  「届かないから再送 → 遅れて届いた1通目のコードが使えない」という
                  ループに陥るため、押す直前に警告を置く。 */}
              <p className="text-[11px] text-slate-500 leading-relaxed text-center">
                再送すると、<strong className="text-industrial-900">前のメールのコードは使えなくなります</strong>。<br />
                再送は1分に1回までです。
              </p>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendCooldown > 0 || isLoading}
                className="text-sm text-safety-600 hover:text-safety-700 disabled:text-slate-400 disabled:cursor-not-allowed font-semibold py-2"
              >
                {isLoading ? '送信中...' : 'コードを再送する'}
              </button>
              <button
                type="button"
                onClick={handleResend}
                className="text-sm text-slate-500 hover:text-slate-700 py-2"
              >
                メールアドレスを変更する
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Email sent confirmation screen
  if (stage === 'linkSent') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <button
              onClick={onBack}
              className="inline-flex items-center space-x-2 mb-4"
            >
              <img src="/icon-512x512.png" alt="" className="h-10 w-10 rounded-lg" />
              <span className="text-2xl font-bold text-industrial-900">安全ポスト＋</span>
            </button>
          </div>

          {/* Success Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>

            <h2 className="text-xl font-bold text-industrial-900 mb-2">
              メールを送信しました
            </h2>

            <p className="text-slate-600 mb-6">
              <span className="font-medium text-industrial-900">{email}</span>
              <br />
              にログインリンクを送信しました。
              <br />
              メールを確認してリンクをクリックしてください。
            </p>

            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 mb-6">
              <p className="mb-2">📧 メールが届かない場合：</p>
              <ul className="text-left list-disc list-inside space-y-1">
                <li>迷惑メールフォルダを確認</li>
                <li>メールアドレスの入力ミスがないか確認</li>
                <li>数分待ってから再送信</li>
              </ul>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleResend}
                className="w-full bg-industrial-900 text-white py-3 rounded-lg font-bold hover:bg-slate-800 transition"
              >
                別のメールアドレスで試す
              </button>
              <button
                onClick={onBack}
                className="w-full text-slate-600 py-2 hover:text-slate-800 transition flex items-center justify-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>ホームに戻る</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <button
            onClick={onBack}
            className="inline-flex items-center space-x-2 mb-4"
          >
            <img src="/icon-512x512.png" alt="" className="h-10 w-10 rounded-lg" />
            <span className="text-2xl font-bold text-industrial-900">安全ポスト＋</span>
          </button>
          <p className="text-slate-600">ログイン / 新規登録</p>
        </div>

        {/* Demo Mode Banner */}
        {!isConfigured && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">デモモード</p>
                <p className="text-xs text-amber-700 mt-1">
                  Supabaseが設定されていません。デモデータでアプリを体験できます。
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
          <div className="text-center mb-6">
            <h2 className="text-lg font-bold text-industrial-900 mb-2">
              メールアドレスで開始
            </h2>
            <p className="text-sm text-slate-600">
              パスワード不要。初めての方も同じフォームから登録できます。<br />
              {/* デモモードはメールを送らずその場でログインする。
                  「コードをお送りします」と書くと挙動と食い違う。 */}
              {isConfigured ? 'メールに6桁の認証コードをお送りします。' : 'デモモードのため、メールは送信せずそのままログインします。'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                メールアドレス
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent transition"
                  required
                  autoFocus
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-industrial-900 text-white py-3 rounded-lg font-bold hover:bg-slate-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>送信中...</span>
                </>
              ) : (
                <span>{isConfigured ? '認証コードを受け取る（無料）' : 'デモモードでログイン'}</span>
              )}
            </button>

            {/* 従来のマジックリンク。廃止せず選択肢として残す。
                法人のメール環境ではリンクが本人より先に開かれて使い切られる
                ことがあるため、既定はコード方式にしている。 */}
            {isConfigured && (
              <button
                type="button"
                onClick={handleSendLink}
                disabled={isLinkLoading || isLoading}
                className="w-full text-sm text-slate-500 hover:text-slate-700 disabled:text-slate-400 py-1 underline underline-offset-2"
              >
                {isLinkLoading ? '送信中...' : 'メールのリンクでログインする'}
              </button>
            )}
            {/* コードとリンクは同一トークンなので、両方を送ると先に送った方が
                無効になる。送信前に伝える。 */}
            {isConfigured && (
              <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                コードとリンクは、どちらか一方をお使いください。<br />
                両方を送ると、先に送った方は使えなくなります。
              </p>
            )}
          </form>

          {/* Google OAuth (hidden in demo mode) */}
          {isConfigured && (
            <>
              <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
                <span className="h-px flex-1 bg-slate-200" />
                または
                <span className="h-px flex-1 bg-slate-200" />
              </div>
              <button
                type="button"
                onClick={handleGoogle}
                disabled={isGoogleLoading || isLoading}
                className="w-full border border-slate-300 rounded-lg py-3 font-medium text-slate-700 flex items-center justify-center space-x-2 hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGoogleLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>接続中...</span>
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5" viewBox="0 0 18 18" aria-hidden="true">
                      <path fill="#4285F4" d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
                      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
                      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
                      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
                    </svg>
                    <span>Googleで続ける</span>
                  </>
                )}
              </button>
            </>
          )}

          {/* Info */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <p className="text-xs text-slate-500 text-center">
              初めての方もこちらからログインできます。
              <br />
              アカウントは自動的に作成されます。
            </p>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <button
            onClick={onBack}
            className="text-sm text-slate-500 hover:text-slate-700 transition flex items-center justify-center space-x-2 mx-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>ホームに戻る</span>
          </button>
        </div>
      </div>
    </div>
  );
}

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function AuthGuard({ children, fallback }: AuthGuardProps) {
  const { isAuthenticated, user } = useAppStore();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isSupabaseConfigured()) {
        setIsChecking(false);
        return;
      }

      const session = await getCurrentSession();
      setIsChecking(false);

      // Session check will trigger store updates via onAuthStateChange
    };

    checkAuth();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-safety-600 mx-auto" />
          <p className="mt-2 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return fallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
}

interface UserAvatarProps {
  name?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function UserAvatar({ name, size = 'md' }: UserAvatarProps) {
  const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base'
  };

  const initials = name
    ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  return (
    <div
      className={`${sizeClasses[size]} bg-gradient-to-br from-safety-500 to-safety-600 rounded-full flex items-center justify-center text-white font-bold`}
    >
      {initials}
    </div>
  );
}

interface UserMenuProps {
  onLogout: () => void;
}

export function UserMenu({ onLogout }: UserMenuProps) {
  const { user, organization } = useAppStore();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-100 transition"
      >
        <UserAvatar name={user?.name} size="sm" />
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-slate-900">{user?.name || 'User'}</p>
          <p className="text-xs text-slate-500">{organization?.name || 'Organization'}</p>
        </div>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-100 py-2 z-50">
            <div className="px-4 py-2 border-b border-slate-100">
              <p className="text-sm font-medium text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
            <div className="px-4 py-2 border-b border-slate-100">
              <div className="flex items-center space-x-2 text-xs text-slate-500">
                <Building2 className="h-3 w-3" />
                <span>{organization?.name}</span>
              </div>
              <div className="mt-1 text-xs text-slate-400 capitalize">
                {organization?.plan} Plan
              </div>
            </div>
            <button
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition"
            >
              Log Out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
